/**
 * RSS 2.0 and RSS 1.0/RDF (M1.2.3).
 *
 * Two formats in one file because they overlap heavily in what they carry and
 * not at all in where they carry it. Worth knowing before reading:
 *
 * - **RSS 2.0 elements are in no namespace.** Not the RSS namespace — none at
 *   all. `<title>` in an RSS 2.0 feed has an empty namespace URI, which is why
 *   every lookup here passes `NS.none` explicitly rather than omitting it.
 * - **RSS 1.0 items are siblings of `channel`, not children.** `rdf:RDF`
 *   contains a `channel` *and* the items, with ordering expressed separately in
 *   `channel/items/rdf:Seq`. A parser written for RSS 2.0 finds nothing in an
 *   RDF feed, which is exactly what `formats/rss1-rdf` records.
 * - **RSS 1.0 identity is `rdf:about`**, not a `guid` element — there is no
 *   `guid` in RSS 1.0 at all.
 */

import type { RawEnclosure, RawFeed, RawItem } from './raw.js';
import type { ParseWarning } from './types.js';
import {
  NS,
  attr,
  element,
  elements,
  firstOf,
  innerMarkup,
  textOf,
  textOrUndefined,
  type XmlElement,
} from './xml.js';

/** `pubDate`, then Dublin Core, then Atom-in-RSS. In that order of intent. */
const DATE_CANDIDATES: readonly (readonly [string, string | undefined])[] = [
  ['pubdate', NS.none],
  ['date', NS.dc],
  ['updated', NS.atom],
  ['issued', NS.dc],
];

/** The bylines feeds actually use, most specific first. */
const AUTHOR_CANDIDATES: readonly (readonly [string, string | undefined])[] = [
  ['creator', NS.dc],
  ['author', NS.none],
  ['author', NS.itunes],
  ['managingeditor', NS.none],
];

export function parseRss2(root: XmlElement): RawFeed {
  const warnings: ParseWarning[] = [];
  const channel = element(root, 'channel', NS.none);

  if (channel === undefined) {
    // An `<rss>` element with no channel. Nothing to read, but the document
    // announced itself as RSS, so this is a malformed feed rather than
    // something that is not a feed at all.
    return {
      format: 'rss2',
      items: [],
      warnings: [{ code: 'not-well-formed', detail: '<rss> contains no <channel>' }],
    };
  }

  const base = attr(root, 'xml:base') ?? attr(channel, 'xml:base');

  return {
    format: 'rss2',
    title: textOrUndefined(element(channel, 'title', NS.none)),
    siteUrl: textOrUndefined(element(channel, 'link', NS.none)),
    // `atom:link rel="self"` inside an RSS feed is common and is the only
    // place an RSS 2.0 feed can state its own address.
    feedUrl: selfLink(channel),
    description: textOrUndefined(element(channel, 'description', NS.none)),
    updated:
      textOrUndefined(element(channel, 'lastbuilddate', NS.none)) ??
      textOrUndefined(element(channel, 'pubdate', NS.none)),
    base,
    items: elements(channel, 'item', NS.none).map((item) => rss2Item(item, base)),
    warnings,
  };
}

function rss2Item(item: XmlElement, feedBase: string | undefined): RawItem {
  const guid = element(item, 'guid', NS.none);

  /*
   * `isPermaLink` defaults to **true** per the RSS 2.0 spec, which is the
   * wrong default for the web — most feeds put an opaque string in `guid` —
   * and is why so many set it explicitly. Honoured as written, because
   * second-guessing it would make identity depend on our judgement rather
   * than the publisher's statement. M1.3 decides what to do with it.
   */
  const isPermaLink = attr(guid, 'ispermalink');

  return {
    guid: textOrUndefined(guid),
    guidIsPermaLink:
      isPermaLink === undefined ? true : isPermaLink.toLowerCase() !== 'false',
    title: textOrUndefined(element(item, 'title', NS.none)),
    link: textOrUndefined(element(item, 'link', NS.none)),
    author: textOrUndefined(firstOf(item, AUTHOR_CANDIDATES)),
    summaryHtml: textOrUndefined(element(item, 'description', NS.none)),
    // `content:encoded` is where a feed puts the full body when `description`
    // is a summary. Where both exist they are genuinely different content.
    contentHtml: textOrUndefined(element(item, 'encoded', NS.content)),
    published: textOrUndefined(firstOf(item, DATE_CANDIDATES)),
    updated: textOrUndefined(element(item, 'updated', NS.atom)),
    enclosures: rssEnclosures(item),
    categories: elements(item, 'category', NS.none)
      .map((category) => textOf(category))
      .filter((value) => value !== ''),
    base: attr(item, 'xml:base') ?? feedBase,
  };
}

export function parseRss1(root: XmlElement): RawFeed {
  const warnings: ParseWarning[] = [];

  /*
   * The channel may be in the RSS 1.0 namespace or — in feeds that declare it
   * as the default namespace and then forget — in none. Both are tried,
   * because rejecting the second would discard real feeds over a namespace
   * declaration nobody reads.
   */
  const channel = element(root, 'channel', NS.rss1) ?? element(root, 'channel', NS.none);
  const base = attr(root, 'xml:base');

  /*
   * Items are siblings of `channel` under `rdf:RDF`, not children of it. Some
   * generators nest them anyway, so both places are searched and the results
   * concatenated — a feed that does both is malformed, not ambiguous.
   */
  const items = [
    ...elements(root, 'item', NS.rss1),
    ...elements(root, 'item', NS.none),
    ...elements(channel, 'item', NS.rss1),
  ];

  if (channel === undefined && items.length === 0) {
    warnings.push({
      code: 'not-well-formed',
      detail: 'rdf:RDF contains no channel and no items',
    });
  }

  const inRss1 = (parent: XmlElement | undefined, name: string): string | undefined =>
    textOrUndefined(element(parent, name, NS.rss1)) ??
    textOrUndefined(element(parent, name, NS.none));

  return {
    format: 'rss1',
    title: inRss1(channel, 'title'),
    siteUrl: inRss1(channel, 'link'),
    feedUrl: selfLink(channel) ?? attr(channel, 'rdf:about'),
    description: inRss1(channel, 'description'),
    updated: textOrUndefined(element(channel, 'date', NS.dc)),
    base,
    items: items.map((item) => rss1Item(item, base)),
    warnings,
  };
}

function rss1Item(item: XmlElement, feedBase: string | undefined): RawItem {
  const inRss1 = (name: string): string | undefined =>
    textOrUndefined(element(item, name, NS.rss1)) ??
    textOrUndefined(element(item, name, NS.none));

  /*
   * `rdf:about` *is* the identity in RSS 1.0, and it is a URI by definition —
   * so it is a permalink, unlike an RSS 2.0 guid. Falling back to `link`
   * because a generator that omits `rdf:about` has left the item with no
   * identifier at all, and the link is the only stable thing remaining.
   */
  const about = attr(item, 'rdf:about');

  return {
    guid: about ?? inRss1('link'),
    guidIsPermaLink: true,
    title: inRss1('title'),
    link: inRss1('link'),
    author: textOrUndefined(firstOf(item, AUTHOR_CANDIDATES)),
    summaryHtml: inRss1('description'),
    contentHtml: textOrUndefined(element(item, 'encoded', NS.content)),
    published: textOrUndefined(firstOf(item, DATE_CANDIDATES)),
    updated: undefined,
    enclosures: rssEnclosures(item),
    categories: [
      ...elements(item, 'subject', NS.dc),
      ...elements(item, 'category', NS.none),
    ]
      .map((node) => textOf(node))
      .filter((value) => value !== ''),
    base: attr(item, 'xml:base') ?? feedBase,
  };
}

/* ----------------------------------------------------------------- shared --- */

/**
 * `atom:link rel="self"`, the only way an RSS feed states its own address.
 *
 * `rel` defaults to `alternate` when absent, so a link with no `rel` is not a
 * self link — treating it as one would record a feed's address as the
 * publisher's home page.
 */
function selfLink(channel: XmlElement | undefined): string | undefined {
  for (const link of elements(channel, 'link', NS.atom)) {
    if ((attr(link, 'rel') ?? 'alternate') === 'self') {
      return attr(link, 'href');
    }
  }
  return undefined;
}

function rssEnclosures(item: XmlElement): readonly RawEnclosure[] {
  const found: RawEnclosure[] = [];

  for (const enclosure of elements(item, 'enclosure', NS.none)) {
    const url = attr(enclosure, 'url');
    if (url !== undefined) {
      found.push({
        url,
        mimeType: attr(enclosure, 'type'),
        length: attr(enclosure, 'length'),
      });
    }
  }

  // Media RSS, which podcast and video feeds use alongside or instead of
  // `enclosure`.
  for (const media of elements(item, 'content', NS.media)) {
    const url = attr(media, 'url');
    if (url !== undefined) {
      found.push({ url, mimeType: attr(media, 'type'), length: attr(media, 'filesize') });
    }
  }

  return found;
}

/** Exported for the Atom parser, which needs the same `rel` defaulting rule. */
export const innerOrText = (node: XmlElement | undefined): string | undefined => {
  const markup = innerMarkup(node);
  return markup === '' ? undefined : markup;
};
